import React, { useState, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp, doc, updateDoc, where, runTransaction } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { createSlug } from '../../utils/slugUtils';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import the stylesheet

// Property Types based on your list
const propertyTypes = [
  "Condominium/Apartment/Flat", "Terrace House", "Semi-Detached", "Bungalow",
  "Townhouse", "Service Residence", "Land", "Commercial Shop", "Office Space",
  "Industrial", "Retail", "SOHO", "SOVO"
];

// Initial state for a new property
export const initialPropertyState = {
  title: '',
  project_name: '',
  property_type: '',
  owner_name: '',
  owner_contact: '',
  location: '',
  price_sale: null,
  price_rent: null,
  listing_type: [],
  land_area: null,
  built_up_area: null,
  bedrooms: null,
  bathrooms: null,
  carparks: null,
  description: '',
  remarks_hidden: '',
  status: 'Available',
  date_status: null,
  images: [],
  is_featured: false,
  display_id: null,
};

// ADD WATERMARK FUNCTION - NEW
const addWatermarkToImage = (file) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Set canvas dimensions to match image
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw the original image
      ctx.drawImage(img, 0, 0);
      
      // Calculate font size based on image dimensions (responsive sizing)
      const baseFontSize = Math.min(img.width, img.height) / 20;
      const fontSize = Math.max(16, Math.min(48, baseFontSize));
      
      // Set up watermark text properties
      const watermarkText = 'penangproperty.kayhooy.com';
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Calculate position (center of image)
      const x = canvas.width / 2;
      const y = canvas.height / 2;
      
      // Add black shadow (multiple layers for stronger shadow)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      const shadowOffset = Math.max(2, fontSize / 16);
      
      // Draw shadow in multiple directions for bold effect
      ctx.fillText(watermarkText, x - shadowOffset, y - shadowOffset);
      ctx.fillText(watermarkText, x + shadowOffset, y - shadowOffset);
      ctx.fillText(watermarkText, x - shadowOffset, y + shadowOffset);
      ctx.fillText(watermarkText, x + shadowOffset, y + shadowOffset);
      ctx.fillText(watermarkText, x - shadowOffset, y);
      ctx.fillText(watermarkText, x + shadowOffset, y);
      ctx.fillText(watermarkText, x, y - shadowOffset);
      ctx.fillText(watermarkText, x, y + shadowOffset);
      
      // Add white text on top
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillText(watermarkText, x, y);
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create a new file with watermark
          const watermarkedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(watermarkedFile);
        } else {
          reject(new Error('Failed to create watermarked image'));
        }
      }, file.type, 0.95); // High quality (95%)
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    // Load the image
    img.src = URL.createObjectURL(file);
  });
};

function PropertyForm({ currentProperty = null, onSaveSuccess }) {
  const [property, setProperty] = useState(currentProperty || initialPropertyState);
  const [newImages, setNewImages] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [processingImages, setProcessingImages] = useState(false); // NEW STATE
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [projectNameOptions, setProjectNameOptions] = useState([]); // New state for project name suggestions

  // Fetch locations for autofill
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locationsCollectionRef = collection(db, 'locations');
        const q = query(locationsCollectionRef, orderBy('name')); // Assuming locations have a 'name' field
        const data = await getDocs(q);
        const fetchedLocationNames = data.docs.map(doc => doc.data().name);
        setLocationOptions(fetchedLocationNames);
        console.log('Fetched locations for autofill:', fetchedLocationNames);
      } catch (err) {
        console.error("Error fetching locations for autofill:", err);
      }
    };

    fetchLocations();
  }, []); // Run once on component mount

  // Fetch project names for autofill
  useEffect(() => {
    const fetchProjectNames = async () => {
      try {
        const projectNamesCollectionRef = collection(db, 'project_names');
        const q = query(projectNamesCollectionRef, orderBy('name')); // Assuming project names have a 'name' field
        const data = await getDocs(q);
        const fetchedProjectNames = data.docs.map(doc => doc.data().name);
        setProjectNameOptions(fetchedProjectNames);
        console.log('Fetched project names for autofill:', fetchedProjectNames);
      } catch (err) {
        console.error("Error fetching project names for autofill:", err);
      }
    };

    fetchProjectNames();
  }, []); // Run once on component mount


  useEffect(() => {
    if (currentProperty) {
      const processedProperty = {
        ...currentProperty,
        price_sale: currentProperty.price_sale ? Number(currentProperty.price_sale) : null,
        price_rent: currentProperty.price_rent ? Number(currentProperty.price_rent) : null,
        land_area: currentProperty.land_area ? Number(currentProperty.land_area) : null,
        built_up_area: currentProperty.built_up_area ? Number(currentProperty.built_up_area) : null,
        bedrooms: currentProperty.bedrooms ? Number(currentProperty.bedrooms) : null,
        bathrooms: currentProperty.bathrooms ? Number(currentProperty.bathrooms) : null,
        carparks: currentProperty.carparks ? Number(currentProperty.carparks) : null,
      };

      setProperty(processedProperty);
      setExistingImageUrls(currentProperty.images || []);
    } else {
      setProperty(initialPropertyState);
      setNewImages([]);
      setExistingImageUrls([]);
    }
  }, [currentProperty]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'listing_type') {
      const selectedOptions = Array.from(e.target.options || [e.target])
        .filter(option => option.selected || (type === 'checkbox' && checked))
        .map(option => option.value);
      setProperty(prev => ({ ...prev, [name]: selectedOptions }));
    } else if (type === 'checkbox') {
      setProperty(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setProperty(prev => ({ ...prev, [name]: value === '' ? null : Number(value) }));
    } else {
      setProperty(prev => ({ ...prev, [name]: value }));
    }
  };

  // NEW HANDLER FOR RICH TEXT EDITOR
  const handleDescriptionChange = (content) => {
    setProperty(prev => ({
      ...prev,
      description: content
    }));
  };

  // MODIFIED IMAGE CHANGE HANDLER WITH WATERMARKING
  const handleImageChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setProcessingImages(true);
      setError(null);
      
      try {
        const files = Array.from(e.target.files);
        const watermarkedFiles = [];
        
        console.log(`Processing ${files.length} images with watermarks...`);
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          console.log(`Processing image ${i + 1}/${files.length}: ${file.name}`);
          
          // Check if it's an image file
          if (!file.type.startsWith('image/')) {
            console.warn(`Skipping non-image file: ${file.name}`);
            continue;
          }
          
          try {
            const watermarkedFile = await addWatermarkToImage(file);
            watermarkedFiles.push(watermarkedFile);
            console.log(`Successfully watermarked: ${file.name}`);
          } catch (err) {
            console.error(`Failed to watermark ${file.name}:`, err);
            // If watermarking fails, use original file
            watermarkedFiles.push(file);
          }
        }
        
        setNewImages(prev => [...prev, ...watermarkedFiles]);
        console.log(`Completed processing. Added ${watermarkedFiles.length} images.`);
        
      } catch (err) {
        console.error("Error processing images:", err);
        setError("Failed to process some images. Please try again.");
      } finally {
        setProcessingImages(false);
        // Clear the file input
        e.target.value = '';
      }
    }
  };

  const handleRemoveNewImage = (indexToRemove) => {
    setNewImages(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleRemoveExistingImage = (urlToRemove) => {
    setExistingImageUrls(prev => prev.filter(url => url !== urlToRemove));
  };

  const uploadImages = async () => {
    setUploadingImages(true);
    const uploadedUrls = [];
    try {
      for (const imageFile of newImages) {
        const storageRef = ref(storage, `property_images/${Date.now()}_${imageFile.name}`);
        const snapshot = await uploadBytes(storageRef, imageFile);
        const downloadURL = await getDownloadURL(snapshot.ref);
        uploadedUrls.push(downloadURL);
      }
      setUploadingImages(false);
      return uploadedUrls;
    } catch (err) {
      console.error("Error uploading images:", err);
      setError("Failed to upload images.");
      setUploadingImages(false);
      return [];
    }
  };

  const handleDeleteOldImages = async (oldUrls, newUrls) => {
    const imagesToDelete = oldUrls.filter(url => !newUrls.includes(url));
    for (const url of imagesToDelete) {
      try {
        const decodedUrl = decodeURIComponent(url);
        const pathStartIndex = decodedUrl.indexOf('/o/property_images/');
        if (pathStartIndex !== -1) {
          const path = decodedUrl.substring(pathStartIndex + 3);
          const questionMarkIndex = path.indexOf('?');
          const finalPath = questionMarkIndex !== -1 ? path.substring(0, questionMarkIndex) : path;

          const imageRef = ref(storage, finalPath);
          await deleteObject(imageRef);
          console.log("Deleted old image from Storage:", finalPath);
        } else {
          console.warn("Could not determine storage path from URL for deletion:", url);
        }
      } catch (err) {
        console.warn("Error deleting old image (might not exist or permissions issue):", url, err);
      }
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setError(null);
  setLoading(true);

  if (!property.title || !property.location || property.listing_type.length === 0) {
    setError("Title, Location, and Listing Type are required.");
    setLoading(false);
    return;
  }

  try {
    // Generate unique slug from title
    const baseSlug = createSlug(property.title);
    let slug = baseSlug;
    let counter = 1;

    // Check if slug exists and make it unique
    while (true) {
      const slugQuery = query(collection(db, 'properties'), where('slug', '==', slug));
      const slugSnapshot = await getDocs(slugQuery);
      if (slugSnapshot.empty) break;
      slug = `${baseSlug}-${counter}`;
      counter++;
    }

    // --- LOCATION AUTOFILL/SAVE LOGIC ---
    const currentEnteredLocation = property.location.trim();
    if (currentEnteredLocation) {
      const locationExistsLocally = locationOptions.some(
        option => option.toLowerCase() === currentEnteredLocation.toLowerCase()
      );

      if (!locationExistsLocally) {
        const locationsCollectionRef = collection(db, 'locations');
        const q = query(locationsCollectionRef, where('name', '==', currentEnteredLocation));
        const existingLocationsSnapshot = await getDocs(q);

        if (existingLocationsSnapshot.empty) {
          try {
            await addDoc(locationsCollectionRef, { name: currentEnteredLocation });
            console.log(`Added new location to Firestore: ${currentEnteredLocation}`);
            setLocationOptions(prev => [...prev, currentEnteredLocation].sort());
          } catch (addError) {
            console.error("Error adding new location to Firestore:", addError);
          }
        }
      }
    }

    // --- PROJECT NAME AUTOFILL/SAVE LOGIC ---
    const currentEnteredProjectName = property.project_name.trim();
    if (currentEnteredProjectName) {
      const projectNameExistsLocally = projectNameOptions.some(
        option => option.toLowerCase() === currentEnteredProjectName.toLowerCase()
      );

      if (!projectNameExistsLocally) {
        const projectNamesCollectionRef = collection(db, 'project_names');
        const q = query(projectNamesCollectionRef, where('name', '==', currentEnteredProjectName));
        const existingProjectNamesSnapshot = await getDocs(q);

        if (existingProjectNamesSnapshot.empty) {
          try {
            await addDoc(projectNamesCollectionRef, { name: currentEnteredProjectName });
            console.log(`Added new project name to Firestore: ${currentEnteredProjectName}`);
            setProjectNameOptions(prev => [...prev, currentEnteredProjectName].sort());
          } catch (addError) {
            console.error("Error adding new project name to Firestore:", addError);
          }
        }
      }
    }

    // Upload new images
    const newUploadedImageUrls = await uploadImages();
    if (uploadingImages) {
      setLoading(false);
      return;
    }

    // Combine existing and new images
    const finalImageUrls = [...existingImageUrls, ...newUploadedImageUrls];

    // Prepare property data
    const propertyData = {
      ...property,
      slug, // Add the generated slug
      images: finalImageUrls,
      date_status: serverTimestamp(),
      price_sale: property.listing_type.includes('sale') ? (property.price_sale || null) : null,
      price_rent: property.listing_type.includes('rent') ? (property.price_rent || null) : null,
      land_area: property.land_area || null,
      built_up_area: property.built_up_area || null,
      bedrooms: property.bedrooms || null,
      bathrooms: property.bathrooms || null,
      carparks: property.carparks || null,
      owner_name: property.owner_name || '',
      owner_contact: property.owner_contact || '',
    };

    if (currentProperty) {
      // Update existing property - don't change display_id
      const propertyDocRef = doc(db, 'properties', currentProperty.id);
      await updateDoc(propertyDocRef, propertyData);
      await handleDeleteOldImages(currentProperty.images || [], finalImageUrls);
      alert('Property updated successfully!');
    } else {
      // Create new property - find highest existing display_id and increment
      let newDisplayId = 1; // Default if no properties exist

      console.log('🔍 STARTING DISPLAY ID CALCULATION...');

      try {
        // Get all properties to find the highest display_id
        console.log('📡 Fetching all properties from database...');
        const allPropertiesQuery = query(collection(db, 'properties'));
        const allPropertiesSnapshot = await getDocs(allPropertiesQuery);
        
        console.log(`📊 Found ${allPropertiesSnapshot.docs.length} total properties in database`);
        
        let maxDisplayId = 0;
        const allDisplayIds = [];
        
        allPropertiesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log(`📄 Property: "${data.title || 'No Title'}" - display_id: ${data.display_id} (type: ${typeof data.display_id})`);
          
          if (data.display_id) {
            allDisplayIds.push(data.display_id);
            if (typeof data.display_id === 'number') {
              maxDisplayId = Math.max(maxDisplayId, data.display_id);
            } else {
              console.warn(`⚠️  Property "${data.title}" has non-number display_id: ${data.display_id}`);
            }
          } else {
            console.warn(`⚠️  Property "${data.title}" has NO display_id`);
          }
        });
        
        console.log('📋 All display_ids found:', allDisplayIds.sort((a, b) => a - b));
        console.log(`🎯 Calculated maxDisplayId: ${maxDisplayId}`);
        
        newDisplayId = maxDisplayId + 1;
        console.log(`✅ New property will get display_id: ${newDisplayId}`);
        
      } catch (err) {
        console.error('❌ Error finding max display_id, using fallback:', err);
        console.error('❌ Full error details:', err.message);
        console.error('❌ Error stack:', err.stack);
        
        // This is where the 7499 came from - the fallback!
        newDisplayId = Date.now() % 10000;
        console.log(`🚨 FALLBACK: Using timestamp-based ID: ${newDisplayId}`);
      }

      // Create the property document with the new display_id
      const newPropertyData = {
        ...propertyData,
        display_id: newDisplayId,
        created_at: serverTimestamp(),
      };

      console.log(`💾 Saving property with display_id: ${newDisplayId}`);
      const docRef = await addDoc(collection(db, 'properties'), newPropertyData);
      console.log(`🎉 Property created successfully! Document ID: ${docRef.id}, Display ID: ${newDisplayId}`);
      alert(`Property added successfully! Display ID: ${newDisplayId}, Slug: ${slug}`);
    }

    onSaveSuccess();
  } catch (err) {
    console.error("❌ Error saving property:", err);
    setError(`Failed to save property: ${err.message}`);
  } finally {
    setLoading(false);
    setNewImages([]);
  }
};


  return (
    <div style={containerStyle}>
      <h2 style={headingStyle}>{currentProperty ? 'Edit Property' : 'Add New Property'}</h2>
      <form onSubmit={handleSubmit} style={formStyle}>
        {error && <p style={errorStyle}>{error}</p>}

        {/* Basic Information */}
        <div style={formRowStyle}>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Title:<span style={requiredStyle}>*</span></label>
            <input
              type="text"
              name="title"
              value={property.title}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Project Name:</label>
            <input
              type="text"
              name="project_name"
              value={property.project_name}
              onChange={handleChange}
              style={inputStyle}
              list="project-name-suggestions"
            />
            <datalist id="project-name-suggestions">
              {projectNameOptions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>
        </div>

        <div style={formRowStyle}>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Property Type:<span style={requiredStyle}>*</span></label>
            <select
              name="property_type"
              value={property.property_type}
              onChange={handleChange}
              style={inputStyle}
              required
            >
              <option value="">Select a type</option>
              {propertyTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Location:<span style={requiredStyle}>*</span></label>
            <input
              type="text"
              name="location"
              value={property.location}
              onChange={handleChange}
              style={inputStyle}
              required
              list="location-suggestions"
            />
            <datalist id="location-suggestions">
              {locationOptions.map((loc) => (
                <option key={loc} value={loc} />
              ))}
            </datalist>
          </div>
        </div>

        {/* Owner Information */}
        <div style={formRowStyle}>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Owner Name:</label>
            <input
              type="text"
              name="owner_name"
              value={property.owner_name || ''}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Owner Contact:</label>
            <input
              type="text"
              name="owner_contact"
              value={property.owner_contact || ''}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Listing Type & Price */}
        <div style={formRowStyle}>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Listing Type:<span style={requiredStyle}>*</span></label>
            <div>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  name="listing_type"
                  value="sale"
                  checked={property.listing_type.includes('sale')}
                  onChange={handleChange}
                  style={checkboxStyle}
                />
                For Sale
              </label>
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  name="listing_type"
                  value="rent"
                  checked={property.listing_type.includes('rent')}
                  onChange={handleChange}
                  style={checkboxStyle}
                />
                For Rent
              </label>
            </div>
          </div>
          <div style={formColumnStyle}>
            {property.listing_type.includes('sale') && (
              <div>
                <label style={labelStyle}>Price (Sale) (RM):</label>
                <input
                  type="number"
                  name="price_sale"
                  value={property.price_sale || ''}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>
            )}
            {property.listing_type.includes('rent') && (
              <div>
                <label style={labelStyle}>Price (Rent) (RM/month):</label>
                <input
                  type="number"
                  name="price_rent"
                  value={property.price_rent || ''}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </div>
            )}
          </div>
        </div>

        {/* Land Area and Built-up Area */}
        <div style={formRowStyle}>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Land Area (sqft):</label>
            <input
              type="number"
              name="land_area"
              value={property.land_area || ''}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Built-up Area (sqft):</label>
            <input
              type="number"
              name="built_up_area"
              value={property.built_up_area || ''}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Bedrooms, Bathrooms, Carparks - 3 Columns */}
        <div style={formRowStyle}>
          <div style={formColumn3Style}>
            <label style={labelStyle}>Bedrooms:</label>
            <input
              type="number"
              name="bedrooms"
              value={property.bedrooms || ''}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div style={formColumn3Style}>
            <label style={labelStyle}>Bathrooms:</label>
            <input
              type="number"
              name="bathrooms"
              value={property.bathrooms || ''}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div style={formColumn3Style}>
            <label style={labelStyle}>Carparks:</label>
            <input
              type="number"
              name="carparks"
              value={property.carparks || ''}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Description & Remarks */}
        <div style={formRowStyle}>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Description:</label>
            <ReactQuill
              theme="snow"
              value={property.description}
              onChange={handleDescriptionChange}
              style={{ height: '200px', marginBottom: '50px' }}
            />
          </div>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Remarks (hidden from public):</label>
            <textarea
              name="remarks_hidden"
              value={property.remarks_hidden}
              onChange={handleChange}
              style={textareaStyle}
              rows="5"
            ></textarea>
          </div>
        </div>

        {/* Status & Featured */}
        <div style={formRowStyle}>
          <div style={formColumnStyle}>
            <label style={labelStyle}>Status:</label>
            <select
              name="status"
              value={property.status}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="Available">Available</option>
              <option value="Sold">Sold</option>
              <option value="Rented">Rented</option>
              <option value="Temporarily Unavailable">Temporarily Unavailable</option>
            </select>
          </div>
          <div style={formColumnStyle}>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                name="is_featured"
                checked={property.is_featured}
                onChange={handleChange}
                style={checkboxStyle}
              />
              Featured Property
            </label>
          </div>
        </div>

        {/* Image Upload - MODIFIED WITH WATERMARK INFO */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Images:</label>
          <div style={imageUploadInfoStyle}>
            <p style={imageUploadNoteStyle}>
              📸 <strong>Automatic Watermarking:</strong> All uploaded images will automatically have "penangproperty.kayhooy.com" watermarked in the center.
            </p>
          </div>
          <input
            type="file"
            multiple
            onChange={handleImageChange}
            accept="image/*"
            style={fileInputStyle}
            disabled={processingImages || uploadingImages}
          />
          {processingImages && (
            <p style={processingStyle}>🔄 Processing images with watermarks...</p>
          )}
          <div style={imagePreviewContainerStyle}>
            {existingImageUrls.map((url, index) => (
              <div key={url} style={imagePreviewItemStyle}>
                <img src={url} alt={`Existing Property ${index}`} style={imageThumbnailStyle} />
                <button
                  type="button"
                  onClick={() => handleRemoveExistingImage(url)}
                  style={removeImageButtonStyle}
                >
                  X
                </button>
              </div>
            ))}
            {newImages.map((file, index) => (
              <div key={file.name + index} style={imagePreviewItemStyle}>
                <img src={URL.createObjectURL(file)} alt={`New Upload ${index}`} style={imageThumbnailStyle} />
                <button
                  type="button"
                  onClick={() => handleRemoveNewImage(index)}
                  style={removeImageButtonStyle}
                >
                  X
                </button>
                <div style={watermarkBadgeStyle}>✨ Watermarked</div>
              </div>
            ))}
            {existingImageUrls.length === 0 && newImages.length === 0 && !processingImages && (
                <p style={{ margin: 0, color: '#666' }}>No images selected</p>
            )}
          </div>
        </div>

        <button type="submit" style={submitButtonStyle} disabled={loading || uploadingImages || processingImages}>
          {processingImages ? 'Processing Images...' : 
           loading || uploadingImages ? 'Saving...' : 
           (currentProperty ? 'Update Property' : 'Add Property')}
        </button>
      </form>
    </div>
  );
}

// Inline Styles (these are correct and do not contain the problematic :hover definitions)
const containerStyle = {
  maxWidth: '900px',
  margin: '20px auto',
  padding: '30px',
  border: '1px solid #ddd',
  borderRadius: '10px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
  fontFamily: 'Arial, sans-serif',
};

const headingStyle = {
  textAlign: 'center',
  color: '#333',
  marginBottom: '25px',
  fontSize: '28px',
};

const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
};

const formRowStyle = {
  display: 'flex',
  gap: '20px',
  marginBottom: '15px',
  flexWrap: 'wrap',
};

const formColumnStyle = {
  flex: 1,
  minWidth: '280px',
  display: 'flex',
  flexDirection: 'column',
};

const formColumn3Style = {
  flex: 1,
  minWidth: '180px',
  display: 'flex',
  flexDirection: 'column',
};

const formGroupStyle = {
  marginBottom: '15px',
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: 'bold',
  color: '#555',
  fontSize: '15px',
};

const requiredStyle = {
  color: 'red',
  marginLeft: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #ccc',
  borderRadius: '6px',
  fontSize: '16px',
  boxSizing: 'border-box',
};

const textareaStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #ccc',
  borderRadius: '6px',
  fontSize: '16px',
  minHeight: '100px',
  resize: 'vertical',
  boxSizing: 'border-box',
};

const checkboxLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '5px',
  color: '#555',
  fontSize: '16px',
};

const checkboxStyle = {
  marginRight: '10px',
  width: '18px',
  height: '18px',
};

const fileInputStyle = {
  display: 'block',
  marginTop: '10px',
};

const imageUploadInfoStyle = {
  backgroundColor: '#f8f9fa',
  border: '1px solid #e9ecef',
  borderRadius: '6px',
  padding: '12px',
  marginBottom: '10px',
};

const imageUploadNoteStyle = {
  margin: 0,
  color: '#495057',
  fontSize: '14px',
};

const processingStyle = {
  color: '#007bff',
  fontWeight: 'bold',
  margin: '10px 0',
  textAlign: 'center',
};

const imagePreviewContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  marginTop: '15px',
  padding: '10px',
  border: '1px dashed #ccc',
  borderRadius: '5px',
  minHeight: '100px',
  justifyContent: 'center',
  alignItems: 'center',
};

const imagePreviewItemStyle = {
  position: 'relative',
  border: '1px solid #eee',
  borderRadius: '5px',
  overflow: 'hidden',
};

const imageThumbnailStyle = {
  width: '100px',
  height: '100px',
  objectFit: 'cover',
  display: 'block',
};

const removeImageButtonStyle = {
  position: 'absolute',
  top: '5px',
  right: '5px',
  backgroundColor: '#dc3545',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: '25px',
  height: '25px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '14px',
  cursor: 'pointer',
  padding: 0,
  lineHeight: '1',
  opacity: 0.8,
  transition: 'opacity 0.2s ease',
};

const watermarkBadgeStyle = {
  position: 'absolute',
  bottom: '2px',
  left: '2px',
  backgroundColor: 'rgba(40, 167, 69, 0.9)',
  color: 'white',
  fontSize: '10px',
  padding: '2px 4px',
  borderRadius: '3px',
  fontWeight: 'bold',
};

const submitButtonStyle = {
  padding: '15px 25px',
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '20px',
  fontWeight: 'bold',
  transition: 'background-color 0.3s ease',
  marginTop: '20px',
};

const errorStyle = {
  color: 'red',
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: '15px',
};

export default PropertyForm;